import { useMemo, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VendorCombobox = ({ vendors, value, onChange, onCreated, allowAll = false, allowCreate = true, testId = "vendor-select" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = vendors.find((v) => v.id === value);
  const results = useMemo(() => { const q = query.trim().toLowerCase(); return q ? vendors.filter((v) => `${v.name} ${v.email || ""} ${v.type || ""}`.toLowerCase().includes(q)) : vendors; }, [vendors, query]);
  const pick = (id) => { onChange(id); setOpen(false); setQuery(""); };
  const create = async () => {
    try {
      const { data } = await axios.post(`${API}/vendors`, { name: query.trim(), type: "merchant" });
      toast.success(`Vendor "${data.name}" added`);
      onCreated && onCreated(data);
      pick(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add vendor"); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal h-10 px-3" data-testid={testId}>
          <span className={`truncate ${selected ? "" : "text-slate-500"}`}>{selected ? selected.name : allowAll && !value ? "All Vendors" : "Select vendor"}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search vendors…" data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>
              <p className="text-slate-500">No vendor matches "{query}"</p>
              {allowCreate && query.trim() && <Button type="button" size="sm" variant="outline" className="mt-3" onClick={create} data-testid={`${testId}-add-new`}><Plus className="w-4 h-4 mr-1" /> Add vendor "{query.trim()}"</Button>}
            </CommandEmpty>
            <CommandGroup>
              {allowAll && !query && <CommandItem value="__all__" onSelect={() => pick("")} data-testid={`${testId}-option-all`}>All Vendors{!value && <Check className="ml-auto h-4 w-4" />}</CommandItem>}
              {results.map((v) => (
                <CommandItem key={v.id} value={v.id} onSelect={() => pick(v.id)} data-testid={`${testId}-option`}>
                  <div className="flex-1 min-w-0"><p className="font-medium truncate">{v.name}</p><p className="text-xs text-slate-500">{v.type}{v.is_1099 ? " · 1099" : ""}</p></div>
                  {v.id === value && <Check className="h-4 w-4 text-indigo-600" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
